import React from 'react';
import type { EnrollmentSession } from '@/types/enrollment';
import MetricChip from './MetricChip';

interface SessionCardProps {
  session: EnrollmentSession;
  isActive?: boolean;
  onClick?: () => void;
}

const qualityColors: Record<string, string> = {
  excellent: '#00d26a',
  good: '#4fc3f7',
  acceptable: '#f5a623',
  poor: '#e74c3c',
  rejected: '#e74c3c',
};

const qualityLabels: Record<string, string> = {
  excellent: 'Excelente',
  good: 'Bom',
  acceptable: 'Aceitavel',
  poor: 'Fraco',
  rejected: 'Rejeitado',
};

/**
 * SessionCard — Card showing a completed enrollment session with mini-stats.
 */
export default function SessionCard({ session, isActive = false, onClick }: SessionCardProps) {
  const isCompleted = session.status === 'completed';
  const isCollecting = session.status === 'collecting';

  return (
    <div
      onClick={onClick}
      className={`
        relative p-4 rounded-card border-2 transition-all duration-200
        ${isActive
          ? 'border-accent-brand bg-bg-surface/50 shadow-lg shadow-accent-brand/10'
          : isCompleted
            ? 'border-bg-surface bg-bg-secondary hover:border-accent-brand/50'
            : 'border-bg-surface/50 bg-bg-secondary/50 opacity-60'
        }
        ${onClick ? 'cursor-pointer' : ''}
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-sans font-medium text-txt-primary">
            Sessao {session.index + 1}
          </span>
          {isCollecting && (
            <span className="flex items-center gap-1 text-xs text-accent-info">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-info animate-pulse-dot" />
              Em andamento
            </span>
          )}
        </div>

        {/* Quality badge */}
        {session.quality && (
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              color: qualityColors[session.quality],
              backgroundColor: `${qualityColors[session.quality]}15`,
            }}
          >
            {qualityLabels[session.quality]}
          </span>
        )}

        {!isCompleted && !isCollecting && (
          <span className="text-xs text-txt-muted">Pendente</span>
        )}
      </div>

      {/* Stats */}
      {isCompleted && (
        <div className="flex flex-wrap gap-2">
          <MetricChip
            label="WPM"
            value={Math.round(session.wpm)}
            color="#4fc3f7"
          />
          <MetricChip
            label="Precisao"
            value={`${(session.accuracy * 100).toFixed(0)}%`}
            color={session.accuracy >= 0.95 ? '#00d26a' : session.accuracy >= 0.85 ? '#f5a623' : '#e74c3c'}
          />
          <MetricChip
            label="Consist."
            value={`${(session.consistency * 100).toFixed(0)}%`}
            color={session.consistency >= 0.7 ? '#00d26a' : '#f5a623'}
          />
        </div>
      )}

      {/* Quality bar */}
      {isCompleted && (
        <div className="mt-3 h-1 bg-bg-surface rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${session.qualityScore}%`,
              backgroundColor: qualityColors[session.quality || 'poor'],
            }}
          />
        </div>
      )}

      {/* Duration */}
      {isCompleted && session.duration > 0 && (
        <div className="mt-2 text-xs text-txt-muted font-sans">
          Duracao: {(session.duration / 1000).toFixed(1)}s
        </div>
      )}
    </div>
  );
}
